import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { registerUser, loginUser, getUser, getCurrentUser, updateUser, deleteUser, getUserActivity } from '../services/userService'
import { fetchCSRFToken, post, clearTokens } from '../services/api'
import { queryClient } from '../plugins/vueQuery'
import logger from '../utils/logger'

/**
 * User Store - Authentication state management
 * 
 * This store manages authentication state and user session.
 * currentUser is in-memory only; initUser() re-fetches from /me on every app boot.
 *
 * Note: For data fetching, prefer using useUserQueries composables
 * from src/queries/useUserQueries which handle caching automatically.
 */
export const useUserStore = defineStore('user', () => {
    // State - Authentication and UI state only
    const currentUser = ref(null)
    const loading = ref(false)
    const error = ref(null)
    const activity = ref([])
    const activityTotal = ref(0)
    const activityLoading = ref(false)
    const sessionExpiredOnInit = ref(false)
    const initialized = ref(false)
    
    // Computed
    const isLoggedIn = computed(() => !!currentUser.value)

    // Basic check for admin role from user object
    const isAdmin = computed(() => {
        if (!currentUser.value) return false
        return currentUser.value.role === 'admin'
    })

    const isModerator = computed(() => {
        if (!currentUser.value) return false
        const r = currentUser.value.role
        return r === 'moderator' || r === 'maintainer' || r === 'admin'
    })

    const isMaintainer = computed(() => {
        if (!currentUser.value) return false
        const r = currentUser.value.role
        return r === 'maintainer' || r === 'admin'
    })

    async function fetchActivity(limit = 10, offset = 0) {
        activityLoading.value = true
        try {
            const response = await getUserActivity({ limit, offset })

            // Note: API returns camelCase keys based on Go structural tags
            const items = response.items || response.Items || []
            const total = response.totalCount || response.TotalCount || 0

            if (offset === 0) {
                activity.value = items
                activityTotal.value = total
            } else {
                activity.value = [...activity.value, ...items]
                // Total remains the same or updates
                activityTotal.value = total
            }
            return response
        } catch (/** @type {any} */ err) {
            // If 401, session is invalid - log out silently
            if (err.status === 401) {
                logout(false)
            }
            logger.error('Failed to fetch activity', err)
            return null
        } finally {
            activityLoading.value = false
        }
    }

    async function initUser(force = false) {
        if (initialized.value && !force) return !!currentUser.value

        loading.value = true

        // Re-validate and fetch authoritative data from backend (cookies)
        try {
            const user = await getCurrentUser()
            currentUser.value = user
        } catch (/** @type {any} */ err) {
            // If /me fails, clear session
            if (err.status === 401 || err.status === 404) {
                if (currentUser.value) {
                    logger.warn('Session invalid, clearing local state')
                }
                currentUser.value = null
            } else {
                logger.error('Failed to validate session:', err)
            }
        } finally {
            loading.value = false
            initialized.value = true
        }
        return !!currentUser.value
    }

    // Actions
    async function register(email, password, profileData = {}, captchaToken = '') {
        loading.value = true
        error.value = null

        try {
            // Fetch CSRF token before making mutating request
            await fetchCSRFToken()

            const userData = { email, password, captcha_token: captchaToken }
            // Add optional profile fields
            if (profileData.username) userData.username = profileData.username
            if (profileData.age_bracket) userData.age_bracket = profileData.age_bracket
            if (profileData.gender) userData.gender = profileData.gender
            if (profileData.country) userData.country = profileData.country

            await registerUser(userData)
            // Registration successful - user needs to verify email now
            return { success: true }
        } catch (/** @type {any} */ err) {
            // Pass through structured error code if available
            return { success: false, error: err.message, code: err.code }
        } finally {
            loading.value = false
        }
    }

    async function login(email, password, captchaToken = '', rememberMe = false) {
        loading.value = true
        error.value = null

        try {
            await loginUser({ email, password, captcha_token: captchaToken, remember_me: rememberMe })

            // Tokens are handled exclusively via HttpOnly cookies by the backend.
            const user = await getCurrentUser()
            currentUser.value = user

            fetchCSRFToken().catch(() => {})

            return { success: true, user }

        } catch (/** @type {any} */ err) {
            error.value = err.message || 'Login failed'
            return { success: false, error: error.value }
        } finally {
            loading.value = false
        }
    }

    async function fetchUser(id) {
        loading.value = true
        error.value = null

        try {
            const user = await getUser(id)
            return user
        } catch (/** @type {any} */ err) {
            error.value = err.message || 'Failed to fetch user'
            return null
        } finally {
            loading.value = false
        }
    }

    async function update(id, data) {
        loading.value = true
        error.value = null

        try {
            await updateUser(id, data)

            // Re-fetch authoritative user data from server to ensure consistency
            if (currentUser.value?.id === id) {
                try {
                    const freshUser = await getCurrentUser()
                    currentUser.value = freshUser
                } catch (fetchErr) {
                    // Fallback: Update local state optimistically if re-fetch fails
                    logger.warn('Failed to re-fetch user after update, using optimistic update:', fetchErr)
                    if (data.email) currentUser.value.email = data.email
                    if (data.username) currentUser.value.username = data.username
                    if (data.age !== undefined) currentUser.value.age = data.age
                    if (data.gender !== undefined) currentUser.value.gender = data.gender
                    if (data.country !== undefined) currentUser.value.country = data.country
                }
            }

            return { success: true }
        } catch (/** @type {any} */ err) {
            error.value = err.message || 'Update failed'
            // If user is not found during update, they might have been deleted
            if (err.status === 404 || err.message?.includes('resource not found')) {
                logger.warn('User not found during update, logging out')
                logout()
            }
            return { success: false, error: error.value }
        } finally {
            loading.value = false
        }
    }

    async function remove(id) {
        loading.value = true
        error.value = null

        try {
            await deleteUser(id)

            // Logout if deleting current user
            if (currentUser.value?.id === id) {
                logout()
            }

            return { success: true }
        } catch (/** @type {any} */ err) {
            error.value = err.message || 'Delete failed'
            return { success: false, error: error.value }
        } finally {
            loading.value = false
        }
    }

    async function logout(callServer = true) {
        const cleanup = () => {
            currentUser.value = null
            clearTokens()
            queryClient.clear()
            // Clear any pending OAuth redirect so the next user on a shared
            // browser doesn't inherit the previous user's saved destination.
            try {
                sessionStorage.removeItem('auth_redirect')
            } catch {
                // sessionStorage unavailable (e.g. SSR, tests)
            }
        }

        if (callServer) {
            try {
                await post('/auth/logout')
            } catch (e) {
                logger.warn('Server logout failed (likely already expired)', e)
            } finally {
                cleanup()
            }
        } else {
            cleanup()
        }
    }

    async function linkGoogle() {
        // Redirect to backend endpoint for Google linking (cookies used for auth)
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8080'
        window.location.href = `${apiUrl}/v1/auth/google/link`
    }

    function clearError() {
        error.value = null
    }

    // Initialize
    // initUser() called by app router/main

    return {
        // State
        currentUser,
        loading,
        error,
        sessionExpiredOnInit,
        initialized,
        // Computed
        isLoggedIn,
        isAdmin,
        isModerator,
        isMaintainer,
        // Actions
        register,
        login,
        fetchUser,
        update,
        remove,
        logout,
        clearError,
        fetchActivity,
        activity,
        activityTotal,
        activityLoading,
        initUser,
        linkGoogle
    }
})
